import type * as PdfJs from 'pdfjs-dist';

/**
 * Page previews with pdf.js (Apache-2.0), loaded on first use so it stays out of the main bundle.
 * Rendering scanned pages is the costly part of the PDF tool (#378: 14 s for 300 thumbnails), so
 * the screens ask only for the pages on screen and cancel those that scroll away; renders run one
 * at a time. pdf.js parses in its own Worker.
 */
export interface PdfThumbnails {
  readonly pages: number;
  /**
   * Renders page `page` (zero-based) into `canvas`, `cssWidth` px wide on screen, turned
   * `extraRotation` degrees clockwise on top of the page's own rotation.
   */
  render(
    page: number,
    canvas: HTMLCanvasElement,
    cssWidth: number,
    extraRotation?: number,
  ): Promise<void>;
  /** Drops a queued render, or stops one under way. */
  cancel(page: number): void;
  destroy(): Promise<void>;
}

/** The render was cancelled before it finished; callers ignore it. */
export class RenderCancelledError extends Error {
  constructor() {
    super('The page render was cancelled');
  }
}

/** Sharp on high-density screens without rendering more than twice the CSS size. */
const MAX_PIXEL_RATIO = 2;

let pdfjsLoaded: Promise<typeof PdfJs> | null = null;

function loadPdfJs(): Promise<typeof PdfJs> {
  pdfjsLoaded ??= (async () => {
    const [pdfjs, worker] = await Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ]);
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    return pdfjs;
  })();
  return pdfjsLoaded;
}

interface Job {
  readonly page: number;
  readonly canvas: HTMLCanvasElement;
  readonly cssWidth: number;
  readonly extraRotation: number;
  readonly resolve: () => void;
  readonly reject: (error: Error) => void;
}

type RenderTask = ReturnType<PdfJs.PDFPageProxy['render']>;

/** One job's render state: `cancel` flips the flag and stops the pdf.js task once it exists. */
interface JobState {
  cancelled: boolean;
  renderTask: RenderTask | null;
}

async function renderJob(doc: PdfJs.PDFDocumentProxy, job: Job, state: JobState): Promise<void> {
  const page = await doc.getPage(job.page + 1);
  if (state.cancelled) throw new RenderCancelledError();
  const ratio = Math.min(MAX_PIXEL_RATIO, globalThis.devicePixelRatio || 1);
  const rotation = (((page.rotate + job.extraRotation) % 360) + 360) % 360;
  const base = page.getViewport({ scale: 1, rotation });
  const viewport = page.getViewport({ scale: (job.cssWidth * ratio) / base.width, rotation });
  job.canvas.width = Math.round(viewport.width);
  job.canvas.height = Math.round(viewport.height);
  state.renderTask = page.render({ canvas: job.canvas, viewport });
  await state.renderTask.promise;
  page.cleanup();
  if (state.cancelled) throw new RenderCancelledError();
}

function renderFailure(error: unknown, cancelled: boolean): Error {
  if (cancelled || (error instanceof Error && error.name === 'RenderingCancelledException')) {
    return new RenderCancelledError();
  }
  return error instanceof Error ? error : new Error(String(error));
}

/** Opens the file with pdf.js, freeing its copy of a protected or broken one before reporting. */
async function openDocument(file: Blob) {
  const pdfjs = await loadPdfJs();
  // No XFA: thumbnails do not need it. pdf.js 6 has no eval-based font path any more (the
  // CVE-2024-4367 class): its build carries neither `isEvalSupported` nor `new Function`.
  const task = pdfjs.getDocument({
    data: new Uint8Array(await file.arrayBuffer()),
    enableXfa: false,
  });
  try {
    return { task, doc: await task.promise };
  } catch (error) {
    // A protected or broken file: free pdf.js's copy of it before reporting.
    await task.destroy();
    throw error;
  }
}

export async function openThumbnails(file: Blob): Promise<PdfThumbnails> {
  const { task, doc } = await openDocument(file);
  const queue: Job[] = [];
  let current: { job: Job; cancel: () => void } | null = null;
  let destroyed = false;

  const next = (): void => {
    if (current || destroyed) return;
    const job = queue.shift();
    if (!job) return;
    const state: JobState = { cancelled: false, renderTask: null };
    current = {
      job,
      cancel: () => {
        state.cancelled = true;
        state.renderTask?.cancel();
      },
    };
    void renderJob(doc, job, state)
      .then(() => job.resolve())
      .catch((error: unknown) => job.reject(renderFailure(error, state.cancelled)))
      .finally(() => {
        current = null;
        next();
      });
  };

  return {
    pages: doc.numPages,
    render(page, canvas, cssWidth, extraRotation = 0) {
      return new Promise<void>((resolve, reject) => {
        if (destroyed) {
          reject(new RenderCancelledError());
          return;
        }
        queue.push({ page, canvas, cssWidth, extraRotation, resolve, reject });
        next();
      });
    },
    cancel(page) {
      for (let i = queue.length - 1; i >= 0; i--) {
        if (queue[i]?.page === page) queue.splice(i, 1)[0]?.reject(new RenderCancelledError());
      }
      if (current?.job.page === page) current.cancel();
    },
    async destroy() {
      destroyed = true;
      for (const job of queue.splice(0)) job.reject(new RenderCancelledError());
      current?.cancel();
      await task.destroy();
    },
  };
}
